// Copyright 2020 Drivetrain Hub LLC
// Gear geometry functions for pixi.js.
//
// References:
//  - Examples: https://pixijs.io/examples/#/graphics/advanced.js
//  - Resizing: https://github.com/pixijs/pixi.js/wiki/v4-Tips,-Tricks,-and-Pitfalls#resizing-renderer
//  - Redrawing: https://pixijs.download/dev/docs/PIXI.Graphics.html
//  - Reduce install: https://medium.com/anvoevodin/how-to-set-up-pixijs-v5-project-with-npm-and-webpack-41c18942c88d

import math from "../math";
import gear from "../gear";
import coord from "../coord";
import {mirrorAboutX, points, rotatePoint2, rotationMatrix2} from "../coord";
import * as PIXI from "../../../config/pixi";
import defaults from "../../../config/defaults";
import requests from "../../../requests";
import handlers from "../../../handlers";

// region HELPERS

// SVG recommended size is 20px x 20px
import * as svgRevolute from "../../img/svg-rotate-ccw.svg"
// import * as svgClutch from "../../img/circle-split.svg"
// import * as svgWeld from "../../img/circle-x.svg"

const REVOLUTE_TEXTURE = PIXI.Texture.from(svgRevolute);
// const CLUTCH_TEXTURE = PIXI.Texture.from(svgClutch);
// const WELD_TEXTURE = PIXI.Texture.from(svgWeld);

const GEAR_LEGEND = {
  tipDiameter: {
    label: 'tip diameter',
    color: '#409EFF',
    pixi: 0x409EFF,
  },
  theoreticalPitchDiameter: {
    label: 'reference diameter',
    color: '#ff9940',
    pixi: 0xff9940,
  },
  protuberanceBoundaryDiameter: {
    label: 'form diameter',
    color: '#8f8f8f',
    pixi: 0x8f8f8f,
  },
  filletBoundaryDiameter: {
    label: 'fillet diameter',
    color: '#40fff5',
    pixi: 0x40fff5,
  },
  baseDiameter: {
    label: 'base diameter',
    color: '#252525',
    pixi: 0x252525,
  },
  rootDiameter: {
    label: 'root diameter',
    color: '#ff4040',
    pixi: 0xff4040,
  },
};
const MESH_LEGEND = {
  loa: {
    label: 'line of action',
    color: '#008BFF',
    pixi: 0x008BFF,
  },
  pitch: {
    label: 'pitch point',
    color: '#FFF04A',
    pixi: 0xFFF04A
  },
  sap: {
    label: 'start of active profile',
    color: '#09D400',
    pixi: 0x09D400
  },
  eap: {
    label: 'end of active profile',
    color: '#FF0038',
    pixi: 0xFF0038
  },
  base: {
    label: '',
    color: '#979797',
    pixi: 0x979797
  }
};
const AXES_LEGEND = {
  x: {
    color: '#FF0038',
    pixi: 0xFF0038
  },
  y: {
    color: '#09D400',
    pixi: 0x09D400
  },
  z: {
    color: '#3e43fe',
    pixi: 0x3e43fe
  },
  grid: {
    color: '#e4e7ed',
    pixi: 0xe4e7ed
  },
};

const AXES_LENGTH = 30;
const AXES_LINEWIDTH = 2;
const GRID_LINEWIDTH = 1;
const DATUM_RADIUS = 6;

// eslint-disable-next-line no-unused-vars
const COLORS_DEBUG = {
  black: 0x0,
  red: 0xff4040,
  teal: 0x40fff5,
  orange: 0xff9940,
  green: 0x006917,
};

function drawArc(graphics, p0, p3, ccw) {
  const arcRadius = Math.hypot(p0.x, p0.y);
  const startAngle = Math.atan2(p0.y, p0.x);
  const endAngle = Math.atan2(p3.y, p3.x);
  graphics.arc(0, 0, arcRadius, startAngle, endAngle, ccw);
}

function drawArcTo(graphics, p0, p2) {
  // Calculate coordinates of the tangent point required by arcTo method, often denoted as (x1, y1).
  // Assumes the circle defining the arc radius is positioned at (0, 0).
  // Arc cannot be more than 180 degrees.
  // https://developer.mozilla.org/en-US/docs/Web/API/CanvasRenderingContext2D/arcTo
  // http://dbp-consulting.com/tutorials/canvas/CanvasArcTo.html
  // https://stackoverflow.com/questions/11623037/canvas-arcto-confusion

  const r = Math.hypot(p0.x, p0.y);
  const startAngle = Math.atan2(p0.y, p0.x);
  const endAngle = Math.atan2(p2.y, p2.x);
  const rangeAngle = endAngle - startAngle;
  const rangeHalfAngle = rangeAngle / 2;

  const midAngle = startAngle + rangeHalfAngle;
  const r1 = r / Math.cos(rangeHalfAngle);
  const x1 = r1 * Math.cos(midAngle);
  const y1 = r1 * Math.sin(midAngle);

  // DEV ONLY - line to control point
  // graphics.lineTo(x1, y1);
  // graphics.lineTo(p2.x, p2.y);

  graphics.arcTo(x1, y1, p2.x, p2.y, r);
}

// UNUSED FUNCTION
// function drawArcLinesTo(graphics, p0, p2, n=5) {
//   // Draw circular arc between points by a series of lines.  See drawArcTo() to draw a true arc curve.
//
//   const r = Math.hypot(p0.x, p0.y);
//   const startAngle = Math.atan2(p0.y, p0.x);
//   let endAngle = Math.atan2(p2.y, p2.x);
//
//   // adjust end angle when range is negative; this issue happens for internal gears
//   const range = startAngle - endAngle;
//   if (range < 0) endAngle -= 2*Math.PI;
//
//   const phiArr = math.linspace(startAngle, endAngle, n);
//   phiArr.slice(1, n).forEach(phi => graphics.lineTo(r * Math.cos(phi), r * Math.sin(phi)))
// }

function drawPoint(graphics, xc, yc, r, fill) {
  // Add a filled point to graphics.
  // Set the desired line style before calling this.

  graphics.beginFill(fill);
  graphics.moveTo(xc, yc);
  graphics.arc(xc, yc, r, 0, 2 * Math.PI);
  graphics.endFill();
}

// UNUSED FUNCTION
// function drawPoints(graphics, ptsArr, size, fill) {
//   // Adds filled points to graphics.
//   // Set the desired line style before calling this.
//   // Helper function for debugging control points used in graphics.
//
//   const r = size / 2;
//   graphics.beginFill(fill);
//   ptsArr.forEach(p => {
//     graphics.moveTo(p.x, p.y);
//     graphics.arc(p.x, p.y, r, 0, 2 * Math.PI);
//   });
//   graphics.endFill();
// }

function drawDatum(graphics, xc, yc, scale, color=0x333333) {
  // Add a datum symbol to graphics.

  // config
  const r = DATUM_RADIUS / scale;
  const existingLineStyle = graphics._lineStyle.clone();
  const existingFill = graphics._fillStyle.clone();
  const angle90 = Math.PI / 2;

  // quadrants I & III
  graphics.lineStyle({width: 1, color: color, native: true}).beginFill(color)
    .moveTo(xc, yc)
    .arc(xc, yc, r, 0, angle90)
    .moveTo(xc, yc)
    .arc(xc, yc, r, 2 * angle90, 3 * angle90)
    .endFill();

  // quadrants II & IV
  graphics.beginFill(0xffffff)
    .moveTo(xc, yc)
    .arc(xc, yc, r, angle90, 2 * angle90)
    .moveTo(xc, yc)
    .arc(xc, yc, r, 3 * angle90, 4 * angle90)
    .endFill();

  // restore original styles
  graphics.lineStyle(existingLineStyle).beginFill(existingFill.color);
}

function drawAxes(graphics, xc, yc, scale, xColor=AXES_LEGEND.x.pixi) {
  // Add a axis vectors to graphics.

  const width = AXES_LINEWIDTH / scale;
  const length = AXES_LENGTH / scale;

  // draw x-vector
  graphics.lineStyle(width, xColor);
  graphics.moveTo(xc, yc);
  graphics.lineTo(length, yc);

  // draw y-vector
  graphics.lineStyle(width, AXES_LEGEND.y.pixi);
  graphics.moveTo(xc, yc);
  graphics.lineTo(xc, length);
}

function normalizeDeltaY(wheelEvent) {
  // Normalize wheel event deltaY value for consistent behavior cross-browser.
  // https://developer.mozilla.org/en-US/docs/Web/API/WheelEvent/deltaMode

  switch (wheelEvent.deltaMode) {
    case 0:
      // delta values are specified in pixels, ex) 150
      return wheelEvent.deltaY / 50;
    case 1:
      // delta values are specified in lines, ex) 3
      return wheelEvent.deltaY;
  }
}

// endregion

// region GEARS

/**
 * Draw arcs for important gear diameters.
 * IMPORTANT: graphics.scale is used to scale linewidth since it cannot be changed after drawn.
 * @param {PIXI.Graphics} graphics - Instance of Graphics object to mutate.
 * @param {Object} - Reference gear parameters.
 * @param {Object} - Gear root parameters.
 * @param {string} unitAngle - Angular units.
 * @return {PIXI.Graphics} - Mutated instance of Graphics object.
 */
function gearCircles(
  graphics, {moduleNormal, pressureAngleNormal, helixAngle, numberOfTeeth, rootDiameter, tipDiameter},
  {filletBoundaryDiameter, protuberanceBoundaryDiameter}, unitAngle=undefined) {
  // PLACEHOLDER {pixi} dashed arcs

  // region PARAMS

  pressureAngleNormal = math.angle2rad(pressureAngleNormal, unitAngle);
  helixAngle = math.angle2rad(helixAngle, unitAngle);

  const moduleTransverse = gear.moduleTransverse1({moduleNormal, helixAngle});
  const theoreticalPitchDiameter = gear.theoreticalPitchDiameter1({numberOfTeeth, moduleTransverse});
  const pressureAngleTransverse = gear.pressureAngleTransverse1({pressureAngleNormal, helixAngle});
  const baseDiameter = gear.baseDiameter1({theoreticalPitchDiameter, pressureAngleTransverse});

  // endregion

  // region CIRCLES

  const lineScale = graphics.scale.x;
  function makeCircle(radius, color) {
    // arc used since circle appear tessellated; native=true helps for thin lines
    graphics.lineStyle(1 / lineScale, color, .6, 0.5, true);
    graphics.arc(0, 0, Math.abs(radius), 0, Math.PI, true);
  }

  graphics.clear();
  filletBoundaryDiameter = filletBoundaryDiameter === protuberanceBoundaryDiameter ? 0: filletBoundaryDiameter;
  makeCircle(filletBoundaryDiameter / 2, GEAR_LEGEND.filletBoundaryDiameter.pixi);
  makeCircle(protuberanceBoundaryDiameter / 2, GEAR_LEGEND.protuberanceBoundaryDiameter.pixi);
  makeCircle(rootDiameter / 2, GEAR_LEGEND.rootDiameter.pixi);
  makeCircle(baseDiameter / 2, GEAR_LEGEND.baseDiameter.pixi);
  makeCircle(theoreticalPitchDiameter / 2, GEAR_LEGEND.theoreticalPitchDiameter.pixi);
  makeCircle(tipDiameter / 2, GEAR_LEGEND.tipDiameter.pixi);

  // endregion

  return graphics;
}

/**
 * Intended use case: display a plain circle when a gear will not calculate.
 * IMPORTANT: graphics.scale is used to scale linewidth since it cannot be changed after drawn.
 * @param graphics {PIXI.Graphics} - instance of Graphics object to mutate.
 * @param radius {number} - radius of circle.
 * @return {PIXI.Graphics} - mutated instance of Graphics object.
 */
function gearExternalBlank(graphics, radius) {
  graphics.clear();

  // draw circle
  graphics
    .lineStyle(defaults.pixi.nativeLineStyle)
    .beginFill(defaults.pixi.fill)
    .drawCircle(0, 0, radius)
    .endFill();

  return graphics;
}

/**
 * Intended use case: display a plain ring when a gear will not calculate.
 * IMPORTANT: graphics.scale is used to scale linewidth since it cannot be changed after drawn.
 * @param graphics {PIXI.Graphics} - instance of Graphics object to mutate.
 * @param radius {number} - radius of hole.
 * @return {PIXI.Graphics} - mutated instance of Graphics object.
 */
function gearInternalBlank(graphics, radius) {

  graphics.clear();

  // draw circle with hole
  graphics
    .lineStyle(defaults.pixi.nativeLineStyle)
    .beginFill(defaults.pixi.fill)
    .drawCircle(0, 0, 2 * radius)
    .beginHole()
    .drawCircle(0, 0, radius)
    .endHole()
    .endFill();

  return graphics;
}

/**
 * Draw graphics for external gear teeth.
 * Teeth may have undercut and a protuberance involute.
 * Median tooth is centered on x-axis. For even tooth counts, the (mean + 0.5) tooth is centered.
 * Origin is at the center of the rotational gear body.
 * References:
 *  - https://www.w3schools.com/tags/canvas_arcto.asp
 *  - https://www.w3.org/TR/SVG/paths.html#PathDataCubicBezierCommands
 *
 * @param {PIXI.Graphics} graphics - Instance of Graphics object to mutate.
 * @param {Object} - Gear tooth data.
 * @param {Object} - Gear root data. Expects coordinates for right-tooth, with the tooth root centered on x-axis.
 * @param {string} unitAngle - Angular units of inputs.
 * @param {number} drawTeeth - Number of teeth to draw, otherwise draw all teeth.
 * @param {number} fill - HEX color to fill gear.
 * @return {PIXI.Graphics} - Mutated instance of Graphics object.
 */
function gearExternal(
  graphics,
  {moduleNormal, pressureAngleNormal, helixAngle, numberOfTeeth, rimDiameter, tipDiameter, toothThicknessNormalFinish},
  {x: xFilletR, y: yFilletR, filletBoundaryDiameter, protuberanceBoundaryDiameter, protuberanceBaseDiameter},
  unitAngle=undefined, drawTeeth=undefined, fill) {

  // region CONFIG

  const nFillet = xFilletR.length;
  const isFullRoot = Math.abs(yFilletR[0]) < 1e-10;

  // config teeth to draw
  if (!drawTeeth) {
    drawTeeth = numberOfTeeth;
  } else {
    drawTeeth = drawTeeth < numberOfTeeth ? drawTeeth : numberOfTeeth;
  }

  // config symmetry of teeth
  const remainderTooth = drawTeeth % 2;
  const halfTeeth = Math.floor(drawTeeth / 2);
  const startTooth = -halfTeeth;  // notice negative such that a tooth on x-axis is "tooth zero"
  const finalTooth = halfTeeth + remainderTooth;

  // endregion

  // region PARAMS

  if (!filletBoundaryDiameter) {
    throw Error('Ill-defined root geometry.  Check that finished tooth thickness is not too thin.');
    // ill-defined root geometry, e.g. thin finished tooth thickness causes step change with involute profile
    // filletBoundaryDiameter = 2 * Math.hypot(xFilletR[lenFillet-1], yFilletR[lenFillet-1]);
    // protuberanceBoundaryDiameter = filletBoundaryDiameter;  // IS THIS ALWAYS TRUE?
  }

  const hasStandardInvolute = protuberanceBoundaryDiameter < tipDiameter;
  const hasProtuberanceInvolute = filletBoundaryDiameter !== protuberanceBoundaryDiameter;

  pressureAngleNormal = math.angle2rad(pressureAngleNormal, unitAngle);
  helixAngle = math.angle2rad(helixAngle, unitAngle);

  // calculate base radius
  const moduleTransverse = gear.moduleTransverse1({moduleNormal, helixAngle});
  const theoreticalPitchDiameter = gear.theoreticalPitchDiameter1({numberOfTeeth, moduleTransverse});
  const pressureAngleTransverse = gear.pressureAngleTransverse1({pressureAngleNormal, helixAngle});
  const baseDiameter = gear.baseDiameter1({theoreticalPitchDiameter, pressureAngleTransverse});
  const baseRadius = baseDiameter / 2;

  // calculate roll angles of profile involute (not protuberance involute)
  const rollAngleInvStart = gear.diameter2RollAngle(baseDiameter, protuberanceBoundaryDiameter);
  const rollAngleInvEnd =  gear.diameter2RollAngle(baseDiameter, tipDiameter);

  // calculate angular tooth thickness and pitch
  const toothThicknessTransverse = gear.toothThicknessTransverse1({toothThicknessNormal: toothThicknessNormalFinish, helixAngle});
  const toothBaseHalfAngle = gear.toothBaseHalfAngleTransverse1({theoreticalPitchDiameter, pressureAngleTransverse, toothThicknessTransverse});
  const pitchAngleTransverse = gear.pitchAngleTransverse1(numberOfTeeth);

  // DEV ONLY
  // console.log('helixAngle', helixAngle);
  // console.log('moduleNormal', moduleNormal);
  // console.log('moduleTransverse', moduleTransverse);
  // console.log('pressureAngleNormal', pressureAngleNormal);
  // console.log('pressureAngleTransverse', pressureAngleTransverse);
  // console.log('theoreticalPitchDiameter', theoreticalPitchDiameter);
  // console.log('baseDiameter', baseDiameter);
  // console.log('rollAngleEnd', rollAngleEnd);
  // console.log('toothThicknessTransverse', toothThicknessTransverse);
  // console.log('toothBaseHalfAngle', toothBaseHalfAngle);
  // console.log('pitchAngleTransverse', pitchAngleTransverse);

  // endregion

  // region CONTROL POINTS

  // involute Bezier control points: left and right profiles
  let rInvPoints = gear.involuteBezierCoeffs(baseRadius, rollAngleInvStart, rollAngleInvEnd, 3);
  let lInvPoints = mirrorAboutX(rInvPoints);

  // root fillet points
  let rFillet = points(xFilletR, yFilletR);
  let lFillet = mirrorAboutX(rFillet).reverse();

  // DEV ONLY
  // console.log('rFillet', rFillet);
  // console.log('lFillet', lFillet);

  // rotation matrices of first tooth; configured so that a tooth is always centered on x-axis
  const pitchAngleStart = pitchAngleTransverse * startTooth;
  const rotHalfPitchStart = rotationMatrix2(pitchAngleTransverse / 2 + pitchAngleStart);
  const rotBaseHalfAngleMinusStart = rotationMatrix2(-toothBaseHalfAngle + pitchAngleStart);
  const rotBaseHalfAnglePlusStart = rotationMatrix2(toothBaseHalfAngle + pitchAngleStart);

  // rotate: RIGHT profile by -0.5 tooth thickness, LEFT profile by +0.5 tooth thickness
  let [rInv0, rInv1, rInv2, rInv3] = rInvPoints.map(p => rotatePoint2(rotBaseHalfAngleMinusStart, p));
  let [lInv0, lInv1, lInv2, lInv3] = lInvPoints.map(p => rotatePoint2(rotBaseHalfAnglePlusStart, p));

  // rotate: BOTH root fillets by +0.5 pitch (to draw them successively)
  rFillet = rFillet.map(p => rotatePoint2(rotHalfPitchStart, p));
  lFillet = lFillet.map(p => rotatePoint2(rotHalfPitchStart, p));

  // endregion

  // region PROTUBERANCE [OPTIONAL]

  let rProt0, rProt1, rProt2, rProt3;
  let lProt0, lProt1, lProt2, lProt3;

  if (hasProtuberanceInvolute) {
    // calculate roll angles of protuberance involute
    const rollAngleProtubStart = gear.diameter2RollAngle(protuberanceBaseDiameter, filletBoundaryDiameter);
    const protuberanceEndDiameter = Math.min(protuberanceBoundaryDiameter, tipDiameter);
    const rollAngleProtubEnd = gear.diameter2RollAngle(protuberanceBaseDiameter, protuberanceEndDiameter);

    // involute Bezier control points: left and right protuberance
    let rProtPoints = gear.involuteBezierCoeffs(protuberanceBaseDiameter / 2, rollAngleProtubStart, rollAngleProtubEnd, 3);
    let lProtPoints = mirrorAboutX(rProtPoints);

    // polar angle correction of protuberance involute
    const rProtFilletPt = rProtPoints[0];
    const phiInvStart = Math.atan2(rProtFilletPt.y, rProtFilletPt.x);
    const filletToothSpaceHalfAngle = Math.atan2(yFilletR[nFillet-1], xFilletR[nFillet-1]);
    const phiErr = filletToothSpaceHalfAngle - phiInvStart;

    // rotation matrices for protuberance involutes
    const rotProtubRStart = rotationMatrix2(phiErr + pitchAngleTransverse / 2 + pitchAngleStart);
    const rotProtubLStart = rotationMatrix2(-phiErr + pitchAngleTransverse / 2 + pitchAngleStart);

    // rotate protuberance involutes
    [rProt0, rProt1, rProt2, rProt3] = rProtPoints.map(p => rotatePoint2(rotProtubRStart, p));
    [lProt0, lProt1, lProt2, lProt3] = lProtPoints.map(p => rotatePoint2(rotProtubLStart, p));
  }

  // endregion

  // region TOOTH CURVES
  //  1. Using lineStyle with native=true do reduce artifacts, but line width is limited to 1.
  //  2. See native linewidth limitation: https://github.com/pixijs/pixi.js/issues/5352
  //  3. Artifacts mainly present for cases with undercut, with or without the protuberance involute.
  //    a. Maybe due to slight inaccuracies in connecting the involutes to the undercut root fillet.
  //  4. This is mostly an issue with internal gears.

  graphics.clear()
    .lineStyle(defaults.pixi.nativeLineStyle)
    .beginFill(fill);

  // DEV ONLY - points for debugging
  // graphics.removeChildren();
  // let dbPts = new Graphics();
  // graphics.addChild(dbPts);
  // const dbPtSize = 2 / graphics.scale.x;

  // move to start of right involute
  graphics.moveTo(rInv0.x, rInv0.y);
  const startPt = rInv0;
  const rotPitch = rotationMatrix2(pitchAngleTransverse);

  // create path for each tooth
  for (let i=startTooth; i < finalTooth; i++) {
    if (hasStandardInvolute) {
      // draw right involute curve from root-to-tip
      graphics.bezierCurveTo(rInv1.x, rInv1.y, rInv2.x, rInv2.y, rInv3.x, rInv3.y);
      // drawPoints(dbPts, [rInv0, rInv1, rInv2, rInv3], dbPtSize, COLORS_DEBUG.red);  // DEBUGGING

      // draw tip arc from right-to-left flank
      drawArcTo(graphics, rInv3, lInv3);

      // draw left involute curve from tip-to-root; connects to left root or protuberance involute
      graphics.bezierCurveTo(lInv2.x, lInv2.y, lInv1.x, lInv1.y, lInv0.x, lInv0.y);
      // drawPoints(dbPts, [lInv3, lInv2, lInv1, lInv0], dbPtSize, COLORS_DEBUG.orange);  // DEBUGGING
    }

    if (hasProtuberanceInvolute) {
      // draw left protuberance involute curve from tip-to-root; connects to left root fillet
      // lProt3 coincident with lInv0, hence why a separate curve is not drawing from lInv0 --> lProt3
      graphics.bezierCurveTo(lProt2.x, lProt2.y, lProt1.x, lProt1.y, lProt0.x, lProt0.y);
      // drawPoints(dbPts, [lProt3, lProt2, lProt1, lProt0], dbPtSize, COLORS_DEBUG.teal);  // DEBUGGING
    }

    // PLACEHOLDER {spline} research how to draw spline through points via quadratic or bezier curve
    // draw root fillet of left flank; connects to start of root arc
    lFillet.slice(1, nFillet).forEach(p => graphics.lineTo(p.x, p.y));  // LOOKS OKAY

    // draw root arc from left-to-right fillet
    if (!isFullRoot) {
      const rFilletStart = rFillet[0];
      const lFilletEnd = lFillet[nFillet - 1];
      drawArcTo(graphics, lFilletEnd, rFilletStart);
    }

    // PLACEHOLDER {spline} research how to draw spline through points via quadratic or bezier curve
    // draw right fillet curve; connects to start of right involute
    rFillet.slice(1, nFillet).forEach(p => graphics.lineTo(p.x, p.y));  // LOOKS OKAY

    // rotate control points of profile involutes
    [rInv0, rInv1, rInv2, rInv3] = [rInv0, rInv1, rInv2, rInv3].map(p => rotatePoint2(rotPitch, p));
    [lInv0, lInv1, lInv2, lInv3] = [lInv0, lInv1, lInv2, lInv3].map(p => rotatePoint2(rotPitch, p));

    if (hasProtuberanceInvolute) {
      // draw right protuberance involute from root-to-tip; connects to right involute
      // rProt3 coincident with rInv0, hence why a separate curve is not drawing from rProt3 --> rInv0
      graphics.bezierCurveTo(rProt1.x, rProt1.y, rProt2.x, rProt2.y, rProt3.x, rProt3.y);
      // drawPoints(dbPts, [rProt0, rProt1, rProt2, rProt3], dbPtSize, COLORS_DEBUG.black);  // DEBUGGING

      // rotate control points of left protuberance involute
      [lProt0, lProt1, lProt2, lProt3] = [lProt0, lProt1, lProt2, lProt3].map(p => rotatePoint2(rotPitch, p));

      // draw tip arc from right-to-left protuberance involute
      if (!hasStandardInvolute) {
        drawArcTo(graphics, rProt3, lProt3);
      }

      // rotate control points of right protuberance involute
      [rProt0, rProt1, rProt2, rProt3] = [rProt0, rProt1, rProt2, rProt3].map(p => rotatePoint2(rotPitch, p));
    }

    // rotate coordinates of root fillets
    rFillet = rFillet.map(p => rotatePoint2(rotPitch, p));
    lFillet = lFillet.map(p => rotatePoint2(rotPitch, p));
  }

  // endregion

  // region RIM CURVES

  if (drawTeeth < numberOfTeeth) {
    // draw semi-circle between start and end of tooth path
    drawArc(graphics, rInv0, startPt, false);
  }
  graphics.closePath();

  if (rimDiameter > 0) {
    graphics.beginHole()
      .drawCircle(0, 0, rimDiameter / 2)
      .endHole();
  }

  // endregion

  graphics.endFill();

  // ensure positive scaling to center median tooth on +X-axis
  const scalePositive = Math.abs(graphics.scale.x);
  graphics.scale.set(scalePositive, scalePositive);

  return graphics
}

/**
 * Draw graphics for internal gear teeth.
 * Teeth with a protuberance involute are not supported.
 * Median tooth is centered on x-axis. For even tooth counts, the (mean + 0.5) tooth is centered.
 * Origin is at the center of the rotational gear body.
 * References:
 *  - https://www.w3schools.com/tags/canvas_arcto.asp
 *  - https://www.w3.org/TR/SVG/paths.html#PathDataCubicBezierCommands
 *
 * @param {PIXI.Graphics} graphics - Instance of Graphics object to mutate.
 * @param {Object} - Gear tooth data as expected by API schema, with negative tooth count and diameters.
 * @param {Object} - Gear root data. Expects coordinates for right-tooth, with the tooth root centered on x-axis.
 * @param {string} unitAngle - Angular units of inputs.
 * @param {number} drawTeeth - Number of teeth to draw, otherwise draw all teeth.
 * @param {number} fill - HEX color to fill gear.
 * @return {PIXI.Graphics} - Mutated instance of Graphics object.
 */
function gearInternal(
  graphics,
  {moduleNormal, pressureAngleNormal, helixAngle, numberOfTeeth, rimDiameter, tipDiameter, toothThicknessNormalFinish},
  {x: xFilletR, y: yFilletR, filletBoundaryDiameter},
  unitAngle=undefined, drawTeeth=undefined, fill) {

  // region CONFIG

  // pre-process params
  const nFillet = xFilletR.length;
  const isFullRoot = Math.abs(yFilletR[0]) < 1e-10;
  const numberOfTeethPositive = -numberOfTeeth;

  // config teeth to draw
  if (!drawTeeth) {
    drawTeeth = numberOfTeethPositive;
  } else {
    drawTeeth = drawTeeth < numberOfTeethPositive ? drawTeeth : numberOfTeethPositive;
  }

  // config symmetry of teeth
  const remainderTooth = drawTeeth % 2;
  const halfTeeth = Math.floor(drawTeeth / 2);
  const startTooth = -halfTeeth;  // notice negative such that a tooth on x-axis is "tooth zero"
  const finalTooth = halfTeeth + remainderTooth;

  // endregion

  // region PARAMS

  pressureAngleNormal = math.angle2rad(pressureAngleNormal, unitAngle);
  helixAngle = math.angle2rad(helixAngle, unitAngle);

  // calculate base radius
  const moduleTransverse = gear.moduleTransverse1({moduleNormal, helixAngle});
  const theoreticalPitchDiameter = gear.theoreticalPitchDiameter1({numberOfTeeth, moduleTransverse});
  const pressureAngleTransverse = gear.pressureAngleTransverse1({pressureAngleNormal, helixAngle});
  const baseDiameter = gear.baseDiameter1({theoreticalPitchDiameter, pressureAngleTransverse});
  const baseRadius = baseDiameter / 2;

  // calculate roll angles of profile involute
  const rollAngleStart = gear.diameter2RollAngle(baseDiameter, filletBoundaryDiameter);
  const rollAngleEnd =  gear.diameter2RollAngle(baseDiameter, tipDiameter);

  // calculate angular tooth thickness and pitch
  const toothThicknessTransverse = gear.toothThicknessTransverse1({toothThicknessNormal: toothThicknessNormalFinish, helixAngle});
  const toothBaseHalfAngle = gear.toothBaseHalfAngleTransverse1({theoreticalPitchDiameter, pressureAngleTransverse, toothThicknessTransverse});
  const pitchAngleTransverse = gear.pitchAngleTransverse1(numberOfTeeth);

  // DEV ONLY
  // console.log('helixAngle', helixAngle);
  // console.log('moduleNormal', moduleNormal);
  // console.log('moduleTransverse', moduleTransverse);
  // console.log('pressureAngleNormal', pressureAngleNormal);
  // console.log('pressureAngleTransverse', pressureAngleTransverse);
  // console.log('theoreticalPitchDiameter', theoreticalPitchDiameter);
  // console.log('baseDiameter', baseDiameter);
  // console.log('rollAngleStart', rollAngleStart);
  // console.log('rollAngleEnd', rollAngleEnd);
  // console.log('toothThicknessTransverse', toothThicknessTransverse);
  // console.log('toothBaseHalfAngle', toothBaseHalfAngle);
  // console.log('pitchAngleTransverse', pitchAngleTransverse);

  // endregion

  // region CONTROL POINTS

  // involute Bezier control points: left and right profiles
  let rInvPoints = gear.involuteBezierCoeffs(baseRadius, rollAngleStart, rollAngleEnd, 3);
  let lInvPoints = mirrorAboutX(rInvPoints);

  // flip fillet x-coord to work with involutes derived from (-) diameters
  xFilletR = xFilletR.map(x => -x);

  // root fillet points
  let rFillet = points(xFilletR, yFilletR);
  let lFillet = mirrorAboutX(rFillet).reverse();

  // DEV ONLY
  // console.log('rInvPoints', rInvPoints);
  // console.log('lInvPoints', lInvPoints);
  // console.log('rFillet', rFillet);
  // console.log('lFillet', lFillet);
  // console.log('pitchAngleTransverse', pitchAngleTransverse);
  // console.log('toothBaseHalfAngle', toothBaseHalfAngle);

  // rotation matrices of first tooth; configured so that a tooth is always centered on x-axis
  const pitchAngleStart = pitchAngleTransverse * startTooth;
  const rotHalfPitchStart = rotationMatrix2(pitchAngleTransverse / 2 + pitchAngleStart);
  const rotBaseHalfAngleMinusStart = rotationMatrix2(-toothBaseHalfAngle + pitchAngleStart);
  const rotBaseHalfAnglePlusStart = rotationMatrix2(toothBaseHalfAngle + pitchAngleStart);

  // rotate: RIGHT profile by -0.5 tooth thickness, LEFT profile by +0.5 tooth thickness
  let [rInv0, rInv1, rInv2, rInv3] = rInvPoints.map(p => rotatePoint2(rotBaseHalfAngleMinusStart, p));
  let [lInv0, lInv1, lInv2, lInv3] = lInvPoints.map(p => rotatePoint2(rotBaseHalfAnglePlusStart, p));

  // rotate: BOTH root fillets by +0.5 pitch (to draw them successively)
  rFillet = rFillet.map(p => rotatePoint2(rotHalfPitchStart, p));
  lFillet = lFillet.map(p => rotatePoint2(rotHalfPitchStart, p));

  // DEV ONLY
  // console.log('[rInv0, rInv1, rInv2, rInv3]', [rInv0, rInv1, rInv2, rInv3]);
  // console.log('[lInv0, lInv1, lInv2, lInv3]', [lInv0, lInv1, lInv2, lInv3]);

  // endregion

  // region RIM CURVES
  //  1. Using lineStyle with native=true do reduce artifacts, but line width is limited to 1.
  //  2. See native linewidth limitation: https://github.com/pixijs/pixi.js/issues/5352
  //  3. Artifacts mainly present for cases with undercut, with or without the protuberance involute.
  //    a. Maybe due to slight inaccuracies in connecting the involutes to the undercut root fillet.

  graphics.clear()
    .lineStyle(defaults.pixi.nativeLineStyle)
    .beginFill(fill)
    .drawCircle(0, 0, Math.abs(rimDiameter / 2));

  // endregion

  // region TOOTH CURVES

  graphics.beginHole();

  // DEV ONLY - points for debugging
  // graphics.removeChildren();
  // let dbPts = new Graphics();
  // graphics.addChild(dbPts);
  // const dbPtSize = 2 / scalePositive;;

  // move to start of right involute
  graphics.moveTo(rInv0.x, rInv0.y);
  const startPt = rInv0;
  const rotPitch = rotationMatrix2(pitchAngleTransverse);

  // create path for each tooth
  for (let i=startTooth; i < finalTooth; i++) {
    // draw right involute curve from root-to-tip
    graphics.bezierCurveTo(rInv1.x, rInv1.y, rInv2.x, rInv2.y, rInv3.x, rInv3.y);
    // drawPoints(dbPts, [rInv0, rInv1, rInv2, rInv3], dbPtSize, COLORS_DEBUG.red);  // DEBUGGING

    // draw tip arc from right-to-left flank; line artifacts are bad without native lineStyle
    drawArcTo(graphics, rInv3, lInv3);

    // draw left involute curve from tip-to-root; connects to root fillet
    graphics.bezierCurveTo(lInv2.x, lInv2.y, lInv1.x, lInv1.y, lInv0.x, lInv0.y);
    // drawPoints(dbPts, [lInv3, lInv2, lInv1, lInv0], dbPtSize, COLORS_DEBUG.orange);  // DEBUGGING

    // PLACEHOLDER {spline} research how to draw spline through points via quadratic or bezier curve
    // draw root fillet of left flank; connects to start of root arc
    lFillet.slice(1, nFillet).forEach(p => graphics.lineTo(p.x, p.y));  // LOOKS OKAY

    // draw root arc from left-to-right fillet
    if (!isFullRoot) {
      const rFilletStart = rFillet[0];
      const lFilletEnd = lFillet[nFillet-1];
      drawArcTo(graphics, lFilletEnd, rFilletStart);
    }

    // rotate control points of profile involutes
    [rInv0, rInv1, rInv2, rInv3] = [rInv0, rInv1, rInv2, rInv3].map(p => rotatePoint2(rotPitch, p));
    [lInv0, lInv1, lInv2, lInv3] = [lInv0, lInv1, lInv2, lInv3].map(p => rotatePoint2(rotPitch, p));

    // PLACEHOLDER {spline} research how to draw spline through points via quadratic or bezier curve
    // draw right fillet curve; connects to start of right involute
    rFillet.slice(1, nFillet).forEach(p => graphics.lineTo(p.x, p.y));  // LOOKS OKAY

    // rotate coordinates of root fillets
    rFillet = rFillet.map(p => rotatePoint2(rotPitch, p));
    lFillet = lFillet.map(p => rotatePoint2(rotPitch, p));
  }

  if (drawTeeth < numberOfTeethPositive) {
    // draw semi-circle between start and end of tooth path
    drawArc(graphics, rInv0, startPt, true);
  }
  graphics.closePath()
    .endHole()
    .endFill();

  // endregion

  // invert scaling to center median tooth on +X-axis
  const scalePositive = Math.abs(graphics.scale.x);
  graphics.scale.set(-scalePositive, -scalePositive);

  return graphics
}

function gearGraphics(
  graphics,
  {moduleNormal, pressureAngleNormal, helixAngle, numberOfTeeth, rimDiameter, tipDiameter, toothThicknessNormalFinish},
  {x: xFilletR, y: yFilletR, filletBoundaryDiameter, protuberanceBoundaryDiameter, protuberanceBaseDiameter},
  unitAngle=undefined, drawTeeth=undefined, fill=undefined) {

  if (!fill) fill = defaults.pixi.fill;

  if (numberOfTeeth < 0) {
    return gearInternal(
      graphics,
      {moduleNormal, pressureAngleNormal, helixAngle, numberOfTeeth, rimDiameter, tipDiameter, toothThicknessNormalFinish},
      {x: xFilletR, y: yFilletR, filletBoundaryDiameter},
      unitAngle, drawTeeth, fill
    )
  } else {
    return gearExternal(
      graphics,
      {moduleNormal, pressureAngleNormal, helixAngle, numberOfTeeth, rimDiameter, tipDiameter, toothThicknessNormalFinish},
      {x: xFilletR, y: yFilletR, filletBoundaryDiameter, protuberanceBoundaryDiameter, protuberanceBaseDiameter},
      unitAngle, drawTeeth, fill
    )
  }
}

// endregion

// region MESHES

/**
 * Calculate the base points tangent to the line of action.
 * @param workingPressureAngleTransverse - Working transverse pressure angle.
 * @param baseRadius1 - Base radius of gear #1.
 * @param xCenter1 - x-coordinate for center of gear #1.
 * @param yCenter1 - y-coordinate for center of gear #1.
 * @param baseRadius2 - Base radius of gear #2.
 * @param xCenter2 - x-coordinate for center of gear #2.
 * @param yCenter2 - y-coordinate for center of gear #2.
 * @return {({x: *, y: number}|{x: number, y: *})[]} Base points of gear #1 and gear #2.
 */
function meshBasePoints(
  workingPressureAngleTransverse, baseRadius1, xCenter1, yCenter1, baseRadius2, xCenter2, yCenter2) {

  const p1 = {
    x: xCenter1 + baseRadius1 * Math.cos(workingPressureAngleTransverse),
    y: yCenter1 - baseRadius1 * Math.sin(workingPressureAngleTransverse)
  };
  const p2 = {
    x: xCenter2 - baseRadius2 * Math.cos(workingPressureAngleTransverse),
    y: yCenter2 + baseRadius2 * Math.sin(workingPressureAngleTransverse)
  };

  return [p1, p2];
}

function meshContactConditions(
  contactSlope, halfWorkingFacewidth, rollDistanceToContactDatum, sapRollDistance, eapRollDistance) {
  // Helper function for meshContactLines.
  // Checks if contact is within the plane of action for the contact line defined by the input args.

  // contact points at left-side of facewidth
  const rollDistanceToContactLeft = contactSlope * -halfWorkingFacewidth + rollDistanceToContactDatum;

  // contact points at right-side of facewidth
  const rollDistanceToContactRight = contactSlope * halfWorkingFacewidth + rollDistanceToContactDatum;

  // check if contact line is in the plane of action
  let inContactBetween, inContactAtSAP, inContactAtEAP, inContact;
  if (contactSlope > 0) {
    // gear #1 has RH helix - contact enters at right facewidth and exits at left facewidth
    inContactBetween = sapRollDistance <= rollDistanceToContactLeft && eapRollDistance >= rollDistanceToContactRight;
    inContactAtSAP = sapRollDistance <= rollDistanceToContactRight && sapRollDistance >= rollDistanceToContactLeft;
    inContactAtEAP = eapRollDistance >= rollDistanceToContactLeft && eapRollDistance <= rollDistanceToContactRight;
    inContact = inContactBetween || inContactAtSAP || inContactAtEAP;
  } else {
    // gear #1 has LH helix - contact enters at left facewidth and exits at right facewidth
    inContactBetween = sapRollDistance <= rollDistanceToContactRight && eapRollDistance >= rollDistanceToContactLeft;
    inContactAtSAP = sapRollDistance <= rollDistanceToContactLeft && sapRollDistance >= rollDistanceToContactRight;
    inContactAtEAP = eapRollDistance >= rollDistanceToContactRight && eapRollDistance <= rollDistanceToContactLeft;
    inContact = inContactBetween || inContactAtSAP || inContactAtEAP;
  }

  return {inContactBetween, inContactAtSAP, inContactAtEAP, inContact};
}

function meshContactXLimits(
  contactSlope, halfWorkingFacewidth, rollDistanceToContactDatum, sapRollDistance, eapRollDistance,
  inContactBetween, inContactAtSAP, inContactAtEAP) {
  // Helper function for meshContactLines.
  // Computes the x-limits of the contact line defined by the input args.

  let contactLineX;
  if (inContactBetween) {
    // entire contact line is within plane of action
    contactLineX = [-halfWorkingFacewidth, halfWorkingFacewidth]
  } else {
    // calculate SAP intersection point; y = m * x + b, where y is known, solve x
    const sapIntersectionX = (sapRollDistance - rollDistanceToContactDatum) / contactSlope;

    // calculate EAP intersection point; y = m * x + b, where y is known, solve x
    const eapIntersectionX = (eapRollDistance - rollDistanceToContactDatum) / contactSlope;

    // get x-limits of contact line
    let contactLineX1, contactLineX2;
    if (contactSlope > 0) {
      // RH helix
      contactLineX1 = inContactAtSAP ? sapIntersectionX : -halfWorkingFacewidth;
      contactLineX2 = inContactAtEAP ? eapIntersectionX : halfWorkingFacewidth;
    } else {
      // LH helix
      contactLineX1 = inContactAtEAP ? eapIntersectionX : -halfWorkingFacewidth;
      contactLineX2 = inContactAtSAP ? sapIntersectionX : halfWorkingFacewidth;
    }
    contactLineX = [contactLineX1, contactLineX2];
  }

  return contactLineX;
}

/**
 * Calculate a function to compute the active contact lines for a given angle of gear #1 rotation.
 * @param {number} workingFacewidth - Working facewidth of gear mesh.
 * @param {number} workingPressureAngleTransverse - Working transverse pressure angle.
 * @param {number} baseHelixAngle1 - Base helix angle of gear #1, including sign of helix.
 * @param {number} baseDiameter1 - Base diameter of gear #1.
 * @param {number} numberOfTeeth1 - Number of teeth on gear #1.
 * @param {Object} sapPoint1 - Point with coordinates for gear #1 start of involute profile.
 * @param {Object} eapPoint1 - Point with coordinates for gear #1 end of involute profile.
 * @return {function(phi1FromPitchPoint:number): [[]]} Function to compute the active contact lines per gear #1 rotation.
 */
function meshContactLines(
  workingFacewidth, workingPressureAngleTransverse,
  baseHelixAngle1, baseDiameter1, numberOfTeeth1,
  sapPoint1, eapPoint1) {
  // NOTES:
  //  1. Gear rotation corresponds to a certain amount of roll distance per the base circle.
  //  2. Roll distance corresponds to distance along the line of action.
  //  3. For a given mesh rotation, roll distance is the same for each gear.

  // region PARAMS

  // base pitch is the distance to the next contact point and is the same for both gears
  const basePitchTransverse = gear.basePitchTransverse1({baseDiameter: baseDiameter1, numberOfTeeth: numberOfTeeth1});
  const baseRadius1 = baseDiameter1 / 2;

  // roll distance from pitch point; computed from gear #1 rotation
  // const rollDistanceFromPitchPointFcn = (phi1FromPitchPoint) => baseRadius1 * phi1FromPitchPoint;

  // find contact point based on roll distance from pitch point, along the plane of action
  // const xContactFcn = (rollDistanceFromPitchPoint) => rollDistanceFromPitchPoint * Math.sin(workingPressureAngleTransverse);
  // const yContactFcn = (rollDistanceFromPitchPoint) => rollDistanceFromPitchPoint * Math.cos(workingPressureAngleTransverse);

  // SAP/EAP roll distances from datum pitch point
  const sapRollDistance = Math.hypot(sapPoint1.x, sapPoint1.y) * Math.sign(sapPoint1.y);
  const eapRollDistance = Math.hypot(eapPoint1.x, eapPoint1.y) * Math.sign(eapPoint1.y);

  const halfWorkingFacewidth = workingFacewidth / 2;
  const contactSlope = Math.tan(baseHelixAngle1);

  // endregion

  return (phi1FromPitchPoint) => {
    // PLACEHOLDER {performance} could add condition for spur gears to simplify while loops

    // compute roll distance of the initial tooth contact point, i.e. phi1=0
    const rollDistanceTooth0 = baseRadius1 * phi1FromPitchPoint;

    // IMPORTANT: set an initial roll distance to search +/- base pitches for contact lines
    const toothPass = rollDistanceTooth0 / basePitchTransverse;
    const rollDistanceInit = rollDistanceTooth0 - Math.floor(toothPass) * basePitchTransverse;

    // define loop vars
    let inContactBetween, inContactAtSAP, inContactAtEAP;
    let contactLineX, contactLineY;
    let contactLines = [];

    // region CONTACT: PITCH-TO-EAP

    // initialize loop vars
    let inContact = true;
    let rollDistanceToContactDatum = rollDistanceInit;

    while (inContact) {
      // check conditions of contact
      ({inContactBetween, inContactAtSAP, inContactAtEAP, inContact} = meshContactConditions(
        contactSlope, halfWorkingFacewidth, rollDistanceToContactDatum, sapRollDistance, eapRollDistance
      ));

      if (inContact) {
        // compute points of contact line
        contactLineX = meshContactXLimits(
          contactSlope, halfWorkingFacewidth, rollDistanceToContactDatum, sapRollDistance, eapRollDistance,
          inContactBetween, inContactAtSAP, inContactAtEAP
        );
        contactLineY = contactLineX.map(x => contactSlope * x + rollDistanceToContactDatum);
        contactLines.push(coord.points(contactLineX, contactLineY));

        // DEV ONLY
        // console.log('inContactBetween', inContactBetween);
        // console.log('inContactAtSAP', inContactAtSAP);
        // console.log('inContactAtEAP', inContactAtEAP);
        // console.log('----------------');
      }

      // go to next (+) tooth point of contact
      rollDistanceToContactDatum += basePitchTransverse;
    }

    // endregion

    // region CONTACT: PITCH-TO-SAP

    // reset loop vars
    inContact = true;
    rollDistanceToContactDatum = rollDistanceInit;

    while (inContact) {
      // go to next (-) tooth point of contact; skips tooth #1
      rollDistanceToContactDatum -= basePitchTransverse;

      // check conditions of contact
      ({inContactBetween, inContactAtSAP, inContactAtEAP, inContact} = meshContactConditions(
        contactSlope, halfWorkingFacewidth, rollDistanceToContactDatum, sapRollDistance, eapRollDistance
      ));

      if (inContact) {
        // compute points of contact line
        contactLineX = meshContactXLimits(
          contactSlope, halfWorkingFacewidth, rollDistanceToContactDatum, sapRollDistance, eapRollDistance,
          inContactBetween, inContactAtSAP, inContactAtEAP
        );
        contactLineY = contactLineX.map(x => contactSlope * x + rollDistanceToContactDatum);
        contactLines.unshift(coord.points(contactLineX, contactLineY));

        // DEV ONLY
        // console.log('inContactBetween', inContactBetween);
        // console.log('inContactAtSAP', inContactAtSAP);
        // console.log('inContactAtEAP', inContactAtEAP);
        // console.log('----------------');
      }
    }

    // endregion

    return contactLines;
  };
}

/**
 * Draw the line of action with the pitch point at (0, 0).
 * Draws entire line of action between base circles, plus draws a line for the active plane of action.
 * Assumes positive rotation of gear #1.
 * @param {PIXI.Graphics} graphics - Instance of Graphics object to mutate.
 * @param moduleNormal
 * @param pressureAngleNormal
 * @param helixAngle1
 * @param numberOfTeeth1
 * @param numberOfTeeth2
 * @param workingPitchDiameter1
 * @param workingPitchDiameter2
 * @param workingFacewidth
 * @param workingPressureAngleTransverse
 * @param sapDiameter1
 * @param sapDiameter2
 * @param eapDiameter1
 * @param eapDiameter2
 * @param {string} unitAngle - Angular units.
 * @return {{contactLines: (function(*): *[]), sapPoint1: {x: number, y: number}, eapPoint1: {x: number, y: number}}}
 */
function meshLineOfAction(
  graphics, moduleNormal, pressureAngleNormal, helixAngle1, numberOfTeeth1, numberOfTeeth2,
  workingPitchDiameter1, workingPitchDiameter2, workingFacewidth, workingPressureAngleTransverse,
  sapDiameter1, sapDiameter2, eapDiameter1, eapDiameter2, unitAngle=undefined) {

  // region GEAR PARAMS

  // common gear params
  helixAngle1 = math.angle2rad(helixAngle1, unitAngle);
  workingPressureAngleTransverse = math.angle2rad(workingPressureAngleTransverse, unitAngle);
  const moduleTransverse = gear.moduleTransverse1({moduleNormal, helixAngle: helixAngle1});
  const pressureAngleTransverse = gear.pressureAngleTransverse1({pressureAngleNormal, helixAngle: helixAngle1});
  const baseHelixAngle1 = gear.helixAngleBase1({pressureAngleTransverse, helixAngle: helixAngle1});

  // gear #1 params
  const theoreticalPitchDiameter1 = gear.theoreticalPitchDiameter1({numberOfTeeth: numberOfTeeth1, moduleTransverse});
  const baseDiameter1 = gear.baseDiameter1({theoreticalPitchDiameter: theoreticalPitchDiameter1, pressureAngleTransverse});
  const baseRadius1 = baseDiameter1 / 2;
  const pitchRadius1 = workingPitchDiameter1 / 2;

  // gear #2 params
  const theoreticalPitchDiameter2 = gear.theoreticalPitchDiameter1({numberOfTeeth: numberOfTeeth2, moduleTransverse});
  const baseDiameter2 = gear.baseDiameter1({theoreticalPitchDiameter: theoreticalPitchDiameter2, pressureAngleTransverse});
  const baseRadius2 = baseDiameter2 / 2;
  const pitchRadius2 = workingPitchDiameter2 / 2;

  // endregion

  // region LOA PARAMS

  // center of gears
  const xCenter1 = -pitchRadius1;
  const yCenter1 = 0;

  const xCenter2 = pitchRadius2;
  const yCenter2 = 0;

  // tangent points on base circles; derived with pitch point at (0, 0)
  const [basePoint1, basePoint2] = meshBasePoints(workingPressureAngleTransverse,
    baseRadius1, xCenter1, yCenter1, baseRadius2, xCenter2, yCenter2);

  // slope-intercept line equation; y = mx + b
  const m = (basePoint2.y - basePoint1.y) / (basePoint2.x - basePoint1.x);
  const b = 0;

  // compute coordinates of SAP & EAP; alternatively use roll distances from pitch point (0, 0)
  const [sapPoint1A] = math.lineCircleIntersections(m, b, sapDiameter1 / 2, xCenter1, yCenter1);
  const [eapPoint1A] = math.lineCircleIntersections(m, b, eapDiameter1 / 2, xCenter1, yCenter1);

  // DEV ONLY
  // console.log('sapPoint1A', sapPoint1A);
  // console.log('sapPoint1B', sapPoint1B);
  // console.log('eapPoint1A', eapPoint1A);
  // console.log('eapPoint1B', eapPoint1B);

  // endregion

  // region CONTACT PARAMS

  // retrieve function to compute contact lines in transverse section for a given angle of rotation relative to pitch point
  const contactLinesFcn = meshContactLines(workingFacewidth, workingPressureAngleTransverse, baseHelixAngle1,
    baseDiameter1, numberOfTeeth1, sapPoint1A, eapPoint1A);

  // DEV ONLY
  // const phi1 = gear.pitchAngleTransverse1(numberOfTeeth1) / 2;
  // console.log(`contact @ phi1=${phi1}`, contact(phi1));
  // console.log('contact @ phi1=0', contact(0));

  // endregion

  // region DRAW

  graphics.clear();

  // get scale of pixi stage
  const scale = graphics.parent.scale.x;
  const linewidth = 3 / scale;

  // adjust base points for internal gearing
  let basePoint1Draw = numberOfTeeth1 > 0 ? basePoint1 : {x: -basePoint1.x, y: -basePoint1.y};
  let basePoint2Draw = numberOfTeeth2 > 0 ? basePoint2 : {x: -basePoint2.x, y: -basePoint2.y};

  // base-to-base line of action
  graphics.lineStyle(linewidth, MESH_LEGEND.base.pixi, .25);
  graphics.moveTo(xCenter1, yCenter1);
  graphics.lineTo(basePoint1Draw.x, basePoint1Draw.y);
  graphics.lineTo(basePoint2Draw.x, basePoint2Draw.y);
  graphics.lineTo(xCenter2, yCenter2);

  // active line of action
  // graphics.lineStyle(linewidth, 0xF6A700);  // bright orange
  graphics.lineStyle(linewidth, MESH_LEGEND.loa.pixi);
  graphics.moveTo(sapPoint1A.x, sapPoint1A.y);
  graphics.lineTo(eapPoint1A.x, eapPoint1A.y);

  // points on line of action
  graphics.lineStyle(0);
  drawPoint(graphics, sapPoint1A.x, sapPoint1A.y, linewidth, MESH_LEGEND.sap.pixi);
  drawPoint(graphics, 0, 0, linewidth, MESH_LEGEND.pitch.pixi);
  drawPoint(graphics, eapPoint1A.x, eapPoint1A.y, linewidth, MESH_LEGEND.eap.pixi);
  drawPoint(graphics, basePoint1Draw.x, basePoint1Draw.y, linewidth, MESH_LEGEND.base.pixi);
  drawPoint(graphics, basePoint2Draw.x, basePoint2Draw.y, linewidth, MESH_LEGEND.base.pixi);

  // endregion

  return {
    sapPoint1: sapPoint1A,
    eapPoint1: eapPoint1A,
    contactLines: contactLinesFcn
  }
}

// endregion

// region SHAFT

/**
 * Draw graphics for shaft frame.
 * @param {PIXI.Graphics} graphics - Instance of Graphics object to mutate.
 * @param {[{__typename, width, diameter, z}]} parts - Array of part data required for 2D-section display.
 * @return {PIXI.Graphics} - Mutated instance of Graphics object.
 */
function shaftFrame(graphics, parts) {
  // PLACEHOLDER {2D} generalize if developing a system-level 2D viewer
  graphics.clear();

  // define condition for ignoring diameters
  const ignoreDiameter = (dia) => dia === 0 || typeof dia === 'undefined';

  // get scale of pixi stage
  const parent = graphics.parent;
  const scale = parent.scale.x;

  // geometry - axial
  const [x0, y0] = [0, 0];  // shaft datum
  const zCoord = parts.map(p => p.z);
  const shaftStart = x0 + Math.min(...zCoord);
  const shaftEnd = x0 + Math.max(...zCoord);

  // geometry - diametral
  let diameter, diameters=[];
  parts.forEach(p => {
    if (p.Id) {
      ({diameter} = handlers.model.partSize(p));
      if (!ignoreDiameter(diameter)) diameters.push(diameter);
    }
  });
  const partDiameterMin = Math.min(...diameters);

  // settings
  const linewidthShaftPreferred = 6 / scale;
  const linewidthShaftLimit = partDiameterMin / 6;  // useful for long shafts
  const linewidthShaftMin = 1 / scale;
  const linewidthShaft = Math.max(Math.min(linewidthShaftPreferred, linewidthShaftLimit), linewidthShaftMin);
  const nodeSize = 1.5 * linewidthShaft;
  const nodeHalfSize = nodeSize / 2;

  // draw concept shaft
  graphics
    .lineStyle(linewidthShaft, defaults.pixi.line)
    .moveTo(shaftStart, y0)
    .lineTo(shaftEnd, y0);

  // draw point at each node; datum symbol used for mounted frames
  let xc, yc;
  graphics.beginFill(0x333333).lineStyle(0);
  parts.forEach(part => {
    if (part.__typename === requests.model.TYPENAME.PART.FRAME) {
      xc = x0 + part.z;
      drawDatum(graphics, xc, y0, scale, defaults.pixi.fill);
    } else {
      xc = x0 + part.z - nodeHalfSize;
      yc = y0 - nodeHalfSize;
      graphics.drawRect(xc, yc, nodeSize, nodeSize);
    }
  });
  graphics.endFill();

  // draw axes and datum
  drawAxes(graphics, x0, y0, scale, AXES_LEGEND.z.pixi);
  drawDatum(graphics, x0, y0, scale);

  return graphics;
}

/**
 * Draw graphics for parts mounted on a shaft.
 * @param {PIXI.Graphics} graphics - Instance of shaft Graphics object to mutate by adding child parts.
 * @param {[{...Part, z}]} parts - Array of part data required for 2D-section display.
 * @return {PIXI.Graphics} - Mutated instance of Graphics object.
 */
function shaftParts(graphics, parts) {
  // PLACEHOLDER {2D} generalize if developing a system-level 2D viewer
  graphics.clear();
  graphics.removeChildren();  // remove parts
  if (!parts) return graphics;  // no parts

  // get scale of pixi stage
  const parent = graphics.parent;
  const scale = parent.scale.x;

  // geometry
  const [xc, yc] = [0, 0];  // shaft datum
  const zNodes = parts.map(p => p.z);
  const shaftStart = xc + Math.min(...zNodes);
  const shaftEnd = xc + Math.max(...zNodes);
  const shaftLength = (shaftEnd - shaftStart);
  const centerlineDelta = 0.1 * shaftLength;

  // settings
  const linewidthGrid = GRID_LINEWIDTH / scale;

  // draw centerline
  graphics
    .lineStyle(linewidthGrid, AXES_LEGEND.grid.pixi)
    .moveTo(Math.min(shaftStart, 0) - centerlineDelta, yc)
    .lineTo(Math.max(shaftEnd, 0) + centerlineDelta, yc);

  // draw each part
  let pixiPart;
  parts.forEach(part => {
    if (!part.Id) return;  // next loop
    pixiPart = handlers.model.pixiGraphicsSide(part);
    pixiPart.position.x = part.z;
    graphics.addChild(pixiPart);
  });

  return graphics;
}

/**
 * Draw side-view graphics of a mounted frame, i.e. shaft or carrier mounted to a shaft.
 * @param {{pitchCircleDiameter}} - Frame geometry data for 2D display.
 * @return {PIXI.Graphics} Graphics object of frame.
 */
export function pixiChildFrameSide({pitchCircleDiameter}) {
  const width = pitchCircleDiameter * defaults.geom.carrier.aspectRatio;
  const rectX = -width / 2;
  const rectY = -pitchCircleDiameter / 2;

  // draw child carrier
  return new PIXI.Graphics()
    .beginFill(defaults.pixi.line).lineStyle(0)  // defaults.pixi.nativeLineStyle
    .drawRect(rectX, rectY, width, pitchCircleDiameter)
    .endFill()
    .beginFill(0x333333).lineStyle(0)
    .drawCircle(0, rectY, width)
    .drawCircle(0, -rectY, width)
    .endFill();
}

/**
 * Draw side-view graphics of external detailed gear.
 * @param {{facewidth, tipDiameter, helixAngle}} - Gear geometry data for 2D display.
 * @return {PIXI.Graphics} Graphics object of external gear.
 */
export function pixiGearExternalSide({facewidth, tipDiameter, helixAngle}) {
  const rectX = -facewidth / 2;
  const rectY = -tipDiameter / 2;

  // define texture; https://pixijs.download/dev/docs/PIXI.Graphics.html#beginTextureFill
  const texture = gearTextureGradient(facewidth, tipDiameter);
  const matrix = new PIXI.Matrix().rotate(-helixAngle);  // sign flipped to give correct orientation from side-view

  return new PIXI.Graphics()
    .lineStyle(defaults.pixi.nativeLineStyle)
    .beginTextureFill({texture, matrix})
    .drawRect(rectX, rectY, facewidth, tipDiameter)
    .endFill();
}

/**
 * Draw side-view graphics of internal detailed gear.
 * @param {{facewidth, tipDiameter, helixAngle}} - Gear geometry data for 2D display.
 * @return {PIXI.Graphics} Graphics object of internal gear.
 */
export function pixiGearInternalSide({facewidth, rimDiameter, tipDiameter, helixAngle}) {
  // make diameters positive; careful not to mutate cache
  const outerDiameter = -rimDiameter;
  const innerDiameter = -tipDiameter;

  // graphics settings
  const rectX = -facewidth / 2;
  const rectY = -innerDiameter / 2;
  const rectYLower = -outerDiameter / 2;
  const rectYUpper = innerDiameter / 2;
  const heightSection = (outerDiameter - innerDiameter) / 2;

  // define texture; https://pixijs.download/dev/docs/PIXI.Graphics.html#beginTextureFill
  const texture = gearTextureGradient(facewidth, innerDiameter);
  const matrix = new PIXI.Matrix().rotate(helixAngle);

  return new PIXI.Graphics()
    .lineStyle(defaults.pixi.nativeLineStyle)
    .beginTextureFill({texture, matrix})
    .drawRect(rectX, rectY, facewidth, innerDiameter)
    .endFill()
    .beginFill(0xC0C4CC)
    .drawRect(rectX, rectYLower, facewidth, heightSection)
    .drawRect(rectX, rectYUpper, facewidth, heightSection)
    .endFill();
}

// HELPERS

/**
 * Helper function to create a pixi texture with gradient colors.
 * To illustrate gear helix angle, call texture in PIXI.Graphics.beginTextureFill with a matrix rotation.
 *  https://pixijs.io/examples/#/textures/gradient-basic.js
 *  https://developer.mozilla.org/en-US/docs/Web/API/CanvasRenderingContext2D/createLinearGradient
 * @param {number} width - Width of gradient rectangle.
 * @param {number} diameter - Height of gradient rectangle.
 * @return {PIXI.Texture}
 */
function gearTextureGradient(width, diameter) {
  // NOTE: Tried to illustrate number of teeth, but gradient become vague for small gears.

  // create canvas element
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = diameter;

  // use canvas2d API to create gradient
  const ctx = canvas.getContext('2d');
  const grd = ctx.createLinearGradient(width / 2, 0, width / 2, diameter);
  grd.addColorStop(0.25, '#95a6bb');
  grd.addColorStop(0.375, '#ffffff');
  grd.addColorStop(0.5, '#95a6bb');
  grd.addColorStop(0.625, '#ffffff');
  grd.addColorStop(0.75, '#95a6bb');

  ctx.fillStyle = grd;
  ctx.fillRect(0, 0, width, diameter);

  // create pixi texture
  const texture = PIXI.Texture.from(canvas);
  canvas.remove();

  return texture;
}

/**
 * Helper function to create a pixi texture with lines pattern.
 * To illustrate gear helix angle, call texture in PIXI.Graphics.beginTextureFill with a matrix rotation.
 *  https://developer.mozilla.org/en-US/docs/Web/API/CanvasRenderingContext2D/createPattern
 * @param {number} width - Width of gradient rectangle.
 * @param {number} diameter - Height of gradient rectangle.
 * @return {PIXI.Texture}
 */
// function gearTextureLines(width, diameter) {
//   // PLACEHOLDER {improve} having issues with obtaining desirable line resolution.  Maybe requires scale.
//
//   // create canvas element
//   const patternCanvas  = document.createElement('canvas');
//   const patternContext  = patternCanvas.getContext('2d');
//   const patternHeight = diameter / 10;  // _TODO_ divide by number of teeth
//   patternCanvas.width = width;
//   patternCanvas.height = patternHeight;
//
//   // give the pattern a background color and draw a horizontal line
//   patternContext.fillStyle = '#95a6bb';  // gunmetal
//   patternContext.lineWidth = patternHeight / 4;
//   patternContext.strokeStyle = 'white';
//   patternContext.fillRect(0, 0, patternCanvas.width, patternCanvas.height);
//   patternContext.moveTo(0, patternHeight / 2);
//   patternContext.lineTo(width, patternHeight / 2);
//   patternContext.stroke();
//
//   // create primary canvas and fill it with the pattern
//   const canvas = document.createElement('canvas');
//   canvas.width = width;
//   canvas.height = diameter;
//
//   const ctx = canvas.getContext('2d');
//   ctx.fillStyle = ctx.createPattern(patternCanvas, 'repeat');
//   ctx.fillRect(0, 0, width, diameter);
//
//   // create pixi texture
//   const texture = PIXI.Texture.from(canvas);
//   patternCanvas.remove();
//   canvas.remove();
//
//   return texture;
// }

// endregion

// region CARRIER

/**
 * Draw graphics for carrier frame.
 * @param {PIXI.Graphics} graphics - Instance of Graphics object to mutate.
 * @param {number} pitchCircleDiameter - Carrier pitch circle diameter.
 * @return {PIXI.Graphics} - Mutated instance of Graphics object.
 */
function carrierFrame(graphics, pitchCircleDiameter) {
  graphics.clear();

  // get scale of pixi stage
  const parent = graphics.parent;
  const scale = parent.scale.x;

  // geometry
  const carrierRadius = pitchCircleDiameter / 2;
  const deltaRadius = carrierRadius * defaults.geom.carrier.deltaDiameter;
  const outerRadius = carrierRadius + deltaRadius;
  const innerRadius = carrierRadius - deltaRadius;
  const axisHalfLength = 1.25 * carrierRadius;
  const [xc, yc] = [0, 0];  // carrier datum

  // draw axis lines
  graphics
    .lineStyle(GRID_LINEWIDTH / scale, AXES_LEGEND.grid.pixi)
    .moveTo(-axisHalfLength, yc)
    .lineTo(axisHalfLength, yc)
    .moveTo(xc, -axisHalfLength)
    .lineTo(xc, axisHalfLength);

  // draw carrier
  graphics
    .lineStyle(0)
    .beginFill(defaults.pixi.line)
    .drawCircle(xc, yc, outerRadius)
    .endFill()
    .beginHole()
    .drawCircle(xc, yc, innerRadius)
    .endHole();

  // draw axes and datum
  drawAxes(graphics, xc, yc, scale);
  drawDatum(graphics, xc, yc, scale);

  return graphics;
}

/**
 * Draw graphics for parts mounted on a carrier.
 * @param {PIXI.Graphics} graphics - Instance of Graphics object to mutate.
 * @param {[{part: object, r: number, phi: number, z: number}]} partsData - Array of part data required for 2D-section display.
 * @param {string} unitAngle - Angular units of inputs.
 * @return {PIXI.Graphics} - Mutated instance of Graphics object.
 */
function carrierParts(graphics, partsData, unitAngle) {
  graphics.clear();
  graphics.removeChildren();  // remove parts and text
  if (!partsData) return graphics;  // no parts

  // get scale of pixi stage
  const parent = graphics.parent;
  const scale = parent.scale.x;
  // const nodeSize = 8 / scale;  // DEPRECATED with square nodes
  // const nodeHalfSize = nodeSize / 2;  // DEPRECATED with square nodes

  // text style
  const fontSize = 24;
  const textStyle = new PIXI.TextStyle({
    fill: '#121212',
    fontSize: fontSize,
    fontFamily: "\"Lucida Sans Unicode\", \"Lucida Grande\", sans-serif"
  });
  const textScale = .75 / scale;  // shrinks text but improves resolution
  const xPadding = 8 * textScale;
  // const fontSizeHalf = 1.2 * fontSize / 2 / scale;  // DEPRECATED tuned to vertically center text
  // const fontSizeQuarter = fontSize / 4 / scale;  // DEPRECATED tuned to horizontally center text

  // draw graphics
  let x, y, pixiPart, pixiNode, pixiText;
  partsData.forEach((item, i) => {
    if (!item.part) return;  // next loop
    [x, y] = coord.cylindricalToCartesian(item.r, item.phi, item.z, unitAngle);

    // draw part
    pixiPart = handlers.model.pixiGraphicsFront(item.part);
    pixiPart.position.x = x;
    pixiPart.position.y = y;

    // draw revolute svg
    const scaleRevolute = 1 / scale;
    pixiNode = new PIXI.Sprite(REVOLUTE_TEXTURE);
    pixiNode.anchor.set(.5);
    pixiNode.setTransform(x, y, scaleRevolute, scaleRevolute);

    // FORGONE draw square nodes
    // pixiNode = new PIXI.Graphics()
    //   .beginFill(0x333333).lineStyle(0)
    //   .drawRect(x-nodeHalfSize, y-nodeHalfSize, nodeSize, nodeSize)
    //   .endFill();

    // draw number near center of part
    pixiText = new PIXI.Text(String(i + 1), textStyle);
    pixiText.position.set(x + xPadding, y);  // DEPRECATED center text: (x - fontSizeQuarter, y - fontSizeHalf)
    pixiText.scale.set(textScale, -textScale);  // maintains text size regardless of graphics size

    // add graphics as children
    graphics.addChild(pixiPart, pixiNode, pixiText);
  });

  return graphics;
}

/**
 * Draw front-view graphics of a mounted frame, i.e. shaft or carrier mounted to a carrier.
 * @param {{pitchCircleDiameter}} - Frame geometry data for 2D display.
 * @return {PIXI.Graphics} Graphics object of frame.
 */
export function pixiChildFrameFront({pitchCircleDiameter}) {
  // Create graphics for child shaft / carrier.  Shaft graphics are not visible.

  const pitchRadius = pitchCircleDiameter / 2;
  const deltaRadius = pitchRadius * defaults.geom.carrier.deltaDiameter;
  const outerRadius = pitchRadius + deltaRadius;
  const innerRadius = pitchRadius - deltaRadius;

  return new PIXI.Graphics()
    .beginFill(defaults.pixi.line)
    .drawCircle(0, 0, outerRadius)
    .endFill()
    .beginHole()
    .drawCircle(0, 0, innerRadius)
    .endHole();
}

/**
 * Draw front-view graphics of external detailed gear.
 * @param {{facewidth, tipDiameter, helixAngle}} - Gear geometry data for 2D display.
 * @return {PIXI.Graphics} Graphics object of external gear.
 */
export function pixiGearExternalFront({rimDiameter, tipDiameter}) {
  // Draws gear tip circle and bore diameter.
  // PLACEHOLDER {2D} illustrate more gear detail, such as tooth count, root dia, pitch dia

  let graphics = new PIXI.Graphics()
    .beginFill(0x95a6bb)
    .drawCircle(0, 0,tipDiameter / 2)
    .endFill();

  // bore hole
  if (rimDiameter > 0) {
    graphics
      .beginHole()
      .drawCircle(0, 0, rimDiameter / 2)
      .endHole();
  }

  return graphics;
}

/**
 * Draw front-view graphics of internal detailed gear.
 * @param {{facewidth, tipDiameter, helixAngle}} - Gear geometry data for 2D display.
 * @return {PIXI.Graphics} Graphics object of internal gear.
 */
export function pixiGearInternalFront({rimDiameter, tipDiameter}) {
  // Draws gear rim circle and hole for tip diameter.
  // PLACEHOLDER {2D} illustrate more gear detail, such as tooth count, root dia, pitch dia

  return new PIXI.Graphics()
    .beginFill(0x95a6bb)
    .drawCircle(0, 0,rimDiameter / 2)
    .endFill()
    .beginHole()
    .drawCircle(0, 0, tipDiameter / 2)
    .endHole();
}

// endregion

export default {
  GEAR_LEGEND: GEAR_LEGEND,
  MESH_LEGEND: MESH_LEGEND,
  normalizeDeltaY: normalizeDeltaY,
  gearCircles: gearCircles,
  gearExternalBlank: gearExternalBlank,
  gearInternalBlank: gearInternalBlank,
  gearGraphics: gearGraphics,
  meshLineOfAction: meshLineOfAction,
  shaftFrame: shaftFrame,
  shaftParts: shaftParts,
  carrierFrame: carrierFrame,
  carrierPlanets: carrierParts,
}
